import { PaymentProviderDto } from '@tiendanube/common';
import {
  PaymentProvidersCode,
  muvemPagoOrPagoNubeIds,
} from '@tiendanube/common/src/domains/payments/providers/PaymentProvidersCode';
import { Country } from '@tiendanube/common/src/enums';
import { IS_PAGO_NUBE_ONBOARDING_ON } from 'App/featuresFlags';
import { useNavegate } from 'App/hooks';
import goToAdmin from 'commons/utils/gotToAdmin';
import { openWindow } from 'commons/utils/window';
import { useGetCountry } from 'domains/Auth/hooks';
import { useNuvemPagoFeatureFlag } from 'domains/NuvemPagoCommons/hooks/useNuvemPagoFeatureFlag';
import { PAYMENTS_ROUTES } from 'domains/Payments/paymentsRoutes';
import { invokeEventPayments } from 'domains/Payments/tracking';
import { OnPaymentProviderActionType } from '../../pages/PaymentProvidersListPage/components/PaymentProviderActionModal';
import useGetPaymentProviderSettingsRoute from '../useGetPaymentProviderSettingsRoute';

interface UseProvidersActionProps {
  paymentProvider: PaymentProviderDto;
  onAction: OnPaymentProviderActionType;
}

export function useProvidersAction({
  paymentProvider: {
    id,
    officialSiteUrl,
    preferencesUrl,
    supportUrl,
    mustCompleteInstallation,
    wasInstalled,
  },
  onAction,
}: Readonly<UseProvidersActionProps>) {
  const { goToSettingsPage } = useGetPaymentProviderSettingsRoute(id);
  const { goTo } = useNavegate();
  const country = useGetCountry();
  const { isFeatureEnabled } = useNuvemPagoFeatureFlag(
    IS_PAGO_NUBE_ONBOARDING_ON,
  );

  const handleCancelInstallation = () => {
    onAction(id, 'uninstall');
  };

  const handleGoToMainPage = () => {
    invokeEventPayments('menuAccount', id);
    if (id === PaymentProvidersCode.pagoNube) {
      goTo('/nuvempago');
    } else if (id === PaymentProvidersCode.nuvemPago) {
      goToAdmin('/nuvempago/dashboard')();
    } else if (officialSiteUrl) {
      openWindow(officialSiteUrl, true);
    }
  };

  const handleEnable = () => {
    invokeEventPayments('install', id);

    if (
      muvemPagoOrPagoNubeIds.includes(id) &&
      !mustCompleteInstallation &&
      !wasInstalled &&
      country === Country.MX
    ) {
      return goTo(PAYMENTS_ROUTES.nuvempagoInitSettings);
    }
    if (
      muvemPagoOrPagoNubeIds.includes(id) &&
      !mustCompleteInstallation &&
      !wasInstalled &&
      country !== Country.MX
    ) {
      return goTo(PAYMENTS_ROUTES.nuvempagoInitSettings);
    }

    if (
      muvemPagoOrPagoNubeIds.includes(id) &&
      wasInstalled &&
      mustCompleteInstallation &&
      country === Country.MX
    ) {
      if (isFeatureEnabled) {
        return goTo('/nuvempago/#/onboarding');
      } else {
        return goTo('/pago-nube-onboarding');
      }
    }

    enableAction();
  };

  const handleDisable = () => {
    invokeEventPayments('menuDisabled', id);
    onAction(id, 'disable');
  };

  const handleGoToSettingPage = () => {
    invokeEventPayments('menuSettings', id);
    goToSettingsPage();
  };

  const handleGoToCompleteInstallation = () => {
    if (preferencesUrl.startsWith('/')) {
      goTo(preferencesUrl);
    } else {
      openWindow(preferencesUrl, true);
    }
  };

  const handleGoToSupport = () => {
    if (supportUrl) openWindow(supportUrl, true);
  };

  const enableAction = () => {
    invokeEventPayments('menuEnable', id);
    onAction(id, 'enable');
  };

  return {
    handleGoToMainPage,
    handleGoToSettingPage,
    handleEnable,
    handleGoToCompleteInstallation,
    handleCancelInstallation,
    handleDisable,
    handleGoToSupport,
  };
}
